import { Injectable } from "@angular/core";
import {
  DigitalAssetGetDto,
  DigitalAssetScreenSize,
  DigitalAssetService,
  MediaContentDto,
  NULL_OBSERVABLE,
  TemplateContentType,
  TemplateService
} from "clearline-api";
import { combineLatest, forkJoin, Observable, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import {
  ContentSettings,
  CustomContentTemplateType,
  ScreenSizesJsonAsset,
  Template,
  TemplatePreviewOptions,
  UrlContentSettings
} from "../../models";

@Injectable({ providedIn: "root" })
export class TemplatePreviewService {
  constructor(private templateService: TemplateService, private digitalAssetService: DigitalAssetService) {}

  getPreviewOptionsByTemplates(templates: Template[]): Observable<TemplatePreviewOptions[]> {
    if (!templates || templates.length === 0) {
      return of([]);
    }
    return forkJoin(templates.map((template) => this.getLottieTemplatePreviewOptions(template))).pipe(
      map((lottieOptions: (TemplatePreviewOptions | null)[]) => {
        return templates.map((item: Template, i: number) => {
          const currentLottieOptions: TemplatePreviewOptions = lottieOptions[i] || ({} as TemplatePreviewOptions);
          const {
            templateContentType,
            id,
            templateContentTypeId,
            configuration,
            generatedBody,
            name,
            templateProperties,
            globalTemplateType
          } = item;
          const customContentTemplateType: CustomContentTemplateType | null =
            (templateProperties as UrlContentSettings)?.ContentType || null;

          return {
            ...currentLottieOptions,
            templateContentType: templateContentType as TemplateContentType,
            customContentTemplateType,
            templateId: id,
            templateContentTypeId,
            parameters: configuration?.parameters,
            generatedBody: generatedBody as string,
            previewTitle: name,
            templateProperties: templateProperties,
            configurationOptions: configuration?.options,
            globalTemplateType
          };
        });
      })
    );
  }

  getLottieTemplatePreviewOptions(template: Template): Observable<TemplatePreviewOptions | null> {
    const { templateContentType } = template;
    const contentSettings: ContentSettings = template.templateProperties as ContentSettings;
    const assetId = contentSettings?.assetId;

    return assetId
      ? this.digitalAssetService.getAsset(assetId).pipe(
          mergeMap((asset: DigitalAssetGetDto) => {
            const contentUrl = (asset as DigitalAssetGetDto)?.contentUrl;

            return combineLatest([of(asset), this.getContentByUrl$(contentUrl)]);
          }),
          map(([digitalAsset, json]: [DigitalAssetGetDto, string]) => {
            const options: TemplatePreviewOptions = {
              contentSettings,
              digitalAsset,
              json,
              templateContentType: templateContentType || TemplateContentType.EditableLottie
            };

            return digitalAsset ? options : null;
          }),
          catchError(() => NULL_OBSERVABLE)
        )
      : NULL_OBSERVABLE;
  }

  getOptionByMediaContent(mediaContent: MediaContentDto): Observable<TemplatePreviewOptions | null> {
    const templateContentType: TemplateContentType = mediaContent.templateContentType;
    const digitalAssets: DigitalAssetGetDto[] = mediaContent?.digitalAssets || [];
    const contentUrlAsset: ScreenSizesJsonAsset = digitalAssets.reduce((acc: ScreenSizesJsonAsset, item: DigitalAssetGetDto) => {
      if (item.configuration) {
        const screenSize: DigitalAssetScreenSize = item.configuration.screenSize;

        acc[screenSize] = item.contentUrl;
      }

      return acc;
    }, {} as ScreenSizesJsonAsset);

    return this.getScreenSizesJsonAsset$(contentUrlAsset).pipe(
      map((screenSizesJsonAsset: ScreenSizesJsonAsset) => {
        return { mediaContent, screenSizesJsonAsset, templateContentType } as TemplatePreviewOptions;
      }),
      catchError(() => NULL_OBSERVABLE)
    );
  }

  private getScreenSizesJsonAsset$(contentUrlAsset: ScreenSizesJsonAsset): Observable<ScreenSizesJsonAsset> {
    const jsonList$: Observable<ScreenSizesJsonAsset | null>[] = Object.keys(contentUrlAsset).map((key: string) => {
      const contentUrl: string = contentUrlAsset[key as DigitalAssetScreenSize] as string;

      return this.getContentByUrl$(contentUrl).pipe(map((json: string) => (json ? { [key as DigitalAssetScreenSize]: json } : null)));
    });

    return forkJoin(jsonList$).pipe(
      map((assetList) => {
        return assetList.reduce((acc, item: ScreenSizesJsonAsset | null) => {
          if (item) {
            acc = { ...acc, ...item };
          }
          return acc;
        }, {} as ScreenSizesJsonAsset) as ScreenSizesJsonAsset;
      })
    );
  }

  private getContentByUrl$(contentUrl: string = ""): Observable<string> {
    if (contentUrl) {
      return this.templateService.getContentByUrl(contentUrl as string).pipe(catchError(() => of("")));
    }

    return of("");
  }
}
